import { useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiButtonGroup,
  MiChip,
  MiIcon,
  MiIconButton,
} from "@miplatform/components";
import { useAnchor } from "@miplatform/hooks";
import { Colors } from "@miplatform/types";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import { Box, Checkbox, Menu, MenuItem, Typography } from "@mui/material";
import {
  AssignmentsUnassignedDisplayMode,
  AssignmentsUnassignedDisplayType,
  UNASSIGNED_CITY_DISPLAY_OPTIONS,
  UNASSIGNED_COMMUNITY_DISPLAY_OPTIONS,
  UNASSIGNED_DISPLAY_OPTIONS,
} from "./AssignmentsCalendar.constants";

interface AssignmentsUnassignedHeaderProps {
  isCollapsed: boolean;
  total: number;
  displayType: AssignmentsUnassignedDisplayType;
  displayMode: AssignmentsUnassignedDisplayMode;
  filters: string[];
  filterOptions: { label: string; value: string }[];
  onFiltersChange: (filters: string[]) => void;
  onDisplayTypeChange: (type: AssignmentsUnassignedDisplayType) => void;
  onDisplayModeChange: (mode: AssignmentsUnassignedDisplayMode) => void;
  onCollapseClick: () => void;
}

export const AssignmentsUnassignedHeader = ({
  isCollapsed,
  total,
  displayMode,
  displayType,
  filters,
  filterOptions,
  onFiltersChange,
  onDisplayTypeChange,
  onDisplayModeChange,
  onCollapseClick,
}: AssignmentsUnassignedHeaderProps) => {
  const { theme, classes } = useStyles();
  const [anchorEl, handleMenuOpen, handleMenuClose] = useAnchor();

  const handleCheckboxChange = (value: string) =>
    onFiltersChange(
      filters.includes(value)
        ? filters.filter((f) => f !== value)
        : [...filters, value]
    );

  const displayModeOptions = useMemo(
    () =>
      displayType === AssignmentsUnassignedDisplayType.City
        ? [...UNASSIGNED_CITY_DISPLAY_OPTIONS]
        : [...UNASSIGNED_COMMUNITY_DISPLAY_OPTIONS],
    [displayType]
  );

  const handleDisplayTypeChange = ({
    value,
  }: {
    value: AssignmentsUnassignedDisplayType;
  }) => onDisplayTypeChange(value);

  const handleDisplayModeChange = ({
    value,
  }: {
    value: AssignmentsUnassignedDisplayMode;
  }) => onDisplayModeChange(value);

  return (
    <Box className={classes.root}>
      {total !== 0 && total && (
        <MiChip
          label={total}
          size="small"
          color="error"
          sx={{ height: 14, fontSize: 11 }}
        />
      )}
      <Typography className={classes.title}>Unassigned Orders</Typography>
      <MiButtonGroup
        condensed
        fontWeight={theme.fontWeight.regular}
        containerHeight={22}
        selected={displayType}
        backgroundColor={theme.palette.grey.mediumLight}
        selectedBackgroundColor={theme.palette.grey.medium}
        selectedFontColor={theme.palette.common.white}
        data={[...UNASSIGNED_DISPLAY_OPTIONS]}
        handleClick={handleDisplayTypeChange}
      />
      <MiButtonGroup
        condensed
        fontWeight={theme.fontWeight.regular}
        containerHeight={22}
        selected={displayMode}
        backgroundColor={theme.palette.grey.mediumLight}
        selectedBackgroundColor={theme.palette.grey.medium}
        selectedFontColor={theme.palette.common.white}
        data={displayModeOptions}
        handleClick={handleDisplayModeChange}
      />
      <MiButton
        title={displayType}
        size="small"
        color={Colors.Grey}
        sx={{
          height: 22,
          minWidth: 80,
          fontWeight: theme.fontWeight.regular,
          backgroundColor: theme.palette.grey.mediumLight,
          justifyContent: "start",
          flex: 1,
          width: 144,
          maxWidth: 144,
        }}
        onClick={handleMenuOpen}
      >
        <Box flex={1} />
        <MiIcon
          path={mdiChevronDown}
          color={theme.palette.grey.main}
          size={0.75}
        />
      </MiButton>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        slotProps={{ paper: { sx: { maxHeight: 200, maxWidth: 144 } } }}
      >
        {filterOptions.map((option) => {
          const isChecked = filters.includes(option.value);

          return (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{
                ...(isChecked && {
                  backgroundColor: theme.palette.primary.light,
                }),
                padding: 0,
                height: 24,
                "&:hover": {
                  backgroundColor: isChecked
                    ? theme.palette.action.hover
                    : theme.palette.primary.light,
                },
              }}
              onClick={() => handleCheckboxChange(option.value)}
            >
              <Box display="flex" alignItems="center" gap={1} p={1}>
                <Checkbox checked={isChecked} size="small" />
                <Typography
                  variant="body2"
                  noWrap
                  fontSize={theme.fontSize.small}
                  sx={{
                    maxWidth: 95,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {option.label}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
      <MiIconButton
        sx={{ padding: 0 }}
        disabled={displayMode !== AssignmentsUnassignedDisplayMode.All}
        onClick={onCollapseClick}
      >
        <MiIcon
          path={isCollapsed ? mdiChevronUp : mdiChevronDown}
          color={theme.palette.common.white}
          size={0.75}
        />
      </MiIconButton>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    color: theme.palette.common.white,
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
    height: 28,
    backgroundColor: theme.palette.grey.darkerAccent,
    borderTopLeftRadius: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1),
    overflowY: "hidden",
  },
  title: {
    fontSize: theme.fontSize.medium,
    fontWeight: theme.fontWeight.semiBold,
    flex: 1,
    minWidth: 122,
  },
}));
